/**
 * Tabla del catálogo de sedes (presentacional).
 *
 * Réplica de `sedes.html` del SSR (`progress/explore_admin_ssr.md`): nombre,
 * ciudad, estado (activa/inactiva) y estadísticas (usuarios/órdenes/pendientes/
 * trabajadores), con acciones Editar/Eliminar por fila. Sin lógica de datos:
 * recibe las sedes y notifica las acciones al padre.
 */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { SedeListItem } from "../types";

interface SedesTableProps {
  sedes: SedeListItem[];
  onEdit: (sede: SedeListItem) => void;
  onDelete: (sede: SedeListItem) => void;
}

export function SedesTable({ sedes, onEdit, onDelete }: SedesTableProps) {
  if (sedes.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay sedes registradas.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Nombre</TableHeaderCell>
          <TableHeaderCell>Ciudad</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell className="text-right">Usuarios</TableHeaderCell>
          <TableHeaderCell className="text-right">Órdenes</TableHeaderCell>
          <TableHeaderCell className="text-right">Pendientes</TableHeaderCell>
          <TableHeaderCell className="text-right">Trabajadores</TableHeaderCell>
          <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sedes.map((sede) => (
          <TableRow key={sede.id}>
            <TableCell>
              <span className="font-medium text-slate-900">{sede.nombre}</span>
            </TableCell>
            <TableCell>{sede.ciudad ?? "—"}</TableCell>
            <TableCell>
              {sede.activa ? (
                <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
                  Activa
                </span>
              ) : (
                <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                  Inactiva
                </span>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {sede.stats.total_users}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {sede.stats.total_orders}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {sede.stats.pending_orders}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {sede.stats.total_workers}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap justify-end gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onEdit(sede)}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onDelete(sede)}
                >
                  Eliminar
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
